import type { MonoTypeOperatorFunction } from "rxjs";
import { catchError, combineLatest, finalize, timer } from "rxjs";
import { map } from "rxjs/operators";

interface SetLoadingStateOptions {
    minimumDuration?: number;
}

type SetMethodName<T extends string> = `set${Capitalize<T>}`;

type Uncapitalize<T extends string> = T extends `${infer F}${infer R}` ? `${Lowercase<F>}${R}` : T;

/**
 * Extrait les noms de méthodes qui correspondent à des setters pour des propriétés booléennes.
 * Les méthodes doivent être de la forme `setXxx` (mais pas `setXxxError` ou `setError`) et accepter un paramètre `boolean`.
 */
type BooleanLoadingKeys<T> = {
    [K in keyof T]: K extends `set${infer Prop}`
        ? Prop extends `${infer First}${string}`
            ? First extends Capitalize<First>
                ? Prop extends "Error" | `${string}Error`
                    ? never
                    : T[K] extends (value: boolean) => void
                      ? Uncapitalize<Prop>
                      : never
                : never
            : never
        : never;
}[keyof T];

/**
 * Extrait les noms de méthodes qui correspondent à des setters pour des propriétés d'erreur.
 * Les méthodes doivent être de la forme `setXxxError` ou `setError` et accepter un paramètre `unknown`.
 */
type BooleanErrorKeys<T> = {
    [K in keyof T]: K extends `set${infer Prop}`
        ? Prop extends `${infer First}${string}`
            ? First extends Capitalize<First>
                ? Prop extends "Error" | `${string}Error`
                    ? T[K] extends (error: unknown) => void
                        ? Uncapitalize<Prop>
                        : never
                    : never
                : never
            : never
        : never;
}[keyof T];

type StoreWithLoadingAndError<TStore, TLoadingKey extends string, TErrorKey extends string> = Record<
    SetMethodName<TErrorKey>,
    (error: unknown) => void
> &
    Record<SetMethodName<TLoadingKey>, (value: boolean) => void> &
    Record<string, unknown> &
    TStore;

/**
 * Détermine la valeur par défaut pour la clé de loading.
 */
type DefaultLoadingKey<TStore> = "loading" extends BooleanLoadingKeys<TStore> & string
    ? "loading"
    : BooleanLoadingKeys<TStore> & string;

/**
 * Détermine la valeur par défaut pour la clé d'erreur.
 */
type DefaultErrorKey<TStore> = "error" extends BooleanErrorKeys<TStore> & string
    ? "error"
    : BooleanErrorKeys<TStore> & string;

/**
 * Capitalise seulement la première lettre d'une chaîne sans modifier le reste.
 */
function capitalizeFirst<T extends string>(str: T): Capitalize<T> {
    if (str.length === 0) {
        return str as Capitalize<T>;
    }

    return `${str.charAt(0).toUpperCase()}${str.slice(1)}` as Capitalize<T>;
}

/**
 * Opérateur RxJS qui gère automatiquement l'état de chargement et d'erreur d'un store NgRx Signals
 * pendant l'exécution d'un observable.
 *
 * @param store - Le store NgRx Signals avec les méthodes setLoading et setError (ou méthodes personnalisées)
 * @param loadingKey - Nom de la propriété de chargement (doit être une propriété booléenne du store, par défaut "loading")
 * @param errorKey - Nom de la propriété d'erreur (doit être une propriété d'erreur du store, par défaut "error")
 * @param options - Options optionnelles (minimumDuration pour éviter les flashes)
 * @returns Un MonoTypeOperatorFunction qui gère automatiquement le loading et les erreurs
 */
export function setLoadingAndError<
    T,
    TStore,
    TLoadingKey extends BooleanLoadingKeys<TStore> = DefaultLoadingKey<TStore>,
    TErrorKey extends BooleanErrorKeys<TStore> = DefaultErrorKey<TStore>
>(
    store: StoreWithLoadingAndError<TStore, TLoadingKey, TErrorKey>,
    loadingKey: TLoadingKey = "loading" as TLoadingKey,
    errorKey: TErrorKey = "error" as TErrorKey,
    options?: SetLoadingStateOptions
): MonoTypeOperatorFunction<T> {
    return (source) => {
        // Construire les noms de méthodes dynamiquement
        const loadingMethodName = `set${capitalizeFirst(loadingKey as string)}`;
        const errorMethodName = `set${capitalizeFirst(errorKey as string)}`;

        // Récupérer les méthodes du store (typées correctement grâce aux génériques)
        const loadingFn = store[loadingMethodName] as (value: boolean) => void;
        const errorFn = store[errorMethodName] as (error: unknown) => void;

        // Activer le loading et réinitialiser l'erreur au début
        loadingFn(true);
        errorFn(null);

        const source$ = source.pipe(
            catchError((error: unknown) => {
                // En cas d'erreur, désactiver le loading et définir l'erreur
                loadingFn(false);
                errorFn(error);
                throw error;
            }),
            finalize(() => {
                // À la complétion, désactiver le loading
                loadingFn(false);
            })
        );

        // Si minimumDuration est défini, utiliser combineLatest avec un timer
        if (options?.minimumDuration !== undefined && options.minimumDuration > 0) {
            return combineLatest([source$, timer(options.minimumDuration)]).pipe(
                map(([value]) => value),
                finalize(() => {
                    // S'assurer que le loading est désactivé même si le timer se termine après
                    loadingFn(false);
                })
            );
        }

        return source$;
    };
}
